type StandardEvent = 'AddPaymentInfo' | 'AddToCart' | 'AddToWishlist' | 'CompleteRegistration'
  | 'Contact' | 'CustomizeProduct' | 'Donate' | 'FindLocation' | 'InitiateCheckout' | 'Lead'
  | 'Purchase' | 'Schedule' | 'Search' | 'StartTrial' | 'SubmitApplication' | 'Subscribe' | 'ViewContent';

interface EventProperties {
  content_category?: string;
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  contents: {
    id: string;
    quantity: number;
  }[];
  currency?: string;
  delivery_category?: 'in_store' | 'curbside' | 'home_delivery';
  num_items?: number;
  predicted_ltv?: number;
  search_string?: string;
  status?: 'completed' | 'updated' | 'viewed' | 'added_to_cart' | 'removed_from_cart' | string;
  value?: number;
  [key: string]: unknown;
}

export default defineNuxtPlugin({
  name: 'kygunco:meta',
  dependsOn: ['kygunco:bus'],
  parallel: true,
  setup: () => {
    const config = useRuntimeConfig().public.meta;
    const { $bus } = useNuxtApp();
    const enabled = config.enabled;

    const { proxy, load, onError } = useScriptMetaPixel({
      scriptOptions: {
        trigger: 'manual',
      },
    });

    /**
     * Since this script is not critical to the site, we'll just catch the exception and do nothing.
     * The API is stubbed, so it will be safe to use but will do nothing without the script.
     */
    onError(noop);

    $bus.once('app:interacted', () => enabled && load());

    $bus.on('product:view', (product, variation) =>
      proxy.fbq('track', 'ViewContent', {
        currency: 'USD',
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: variation?.price ?? product.price,
      }),
    );

    $bus.on('product:add', (product, variation, quantity) =>
      proxy.fbq('track', 'AddToCart', {
        currency: 'USD',
        value: (variation?.price ?? product.price) * (quantity ?? 1),
        content_ids: [product.id],
        content_type: 'product',
      }),
    );

    $bus.on('catalog:add', (product, collection) =>
      proxy.fbq('track', 'AddToCart', {
        currency: 'USD',
        value: product.price,
        content_ids: [product.id],
        content_type: 'product',
        content_category: collection,
      }),
    );

    $bus.on('catalog:search', search_string => proxy.fbq('track', 'Search', { search_string }));

    $bus.on('checkout:complete', (orders) => {
      for (const order of orders) {
        proxy.fbq('track', 'Purchase', {
          currency: 'USD',
          value: order.grandTotal,
          content_ids: order.items.map(i => i.productId),
          content_type: 'product',
        }, { eventID: order.id });
      }
    });

    const track = (event: StandardEvent, data?: EventProperties) =>
      proxy.fbq('track', event, data);

    return {
      provide: {
        meta: {
          enabled,
          track,
        },
      },
    };
  },
});
